import ClearIcon from '@mui/icons-material/Clear';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import dayjs from 'dayjs';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IDateRange, IHistoryFilterPanelProps } from '../../../types';
import { EAssociationType, EPropertyActionType, ERecordLength } from '../../data/Constants';

/**
 * Renders the history filter panel allowing users to filter property history
 * by association type, action type, and a bounded date range.
 *
 * @param props - Contains the optional `property` context and the `fetchPropertyHistory` callback.
 * @returns JSX element rendering the filter controls and submit behavior.
 */
export const HistoryFilterPanel = ({
  property,
  fetchPropertyHistory,
  setPage,
  limit,
  isReset,
}: IHistoryFilterPanelProps): ReactElement => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [dateRange, setDateRange] = useState<IDateRange>({});

  const [associationType, setAssociationType] = useState('');

  const [actionType, setActionType] = useState('');

  useEffect(() => {
    if (isReset) {
      setAssociationType('');
      setActionType('');
      setDateRange({ start: undefined, end: undefined });
    }
  }, [isReset]);
  /**
   * Updates the association type filter. Triggers a data refresh if
   * this clears the final active filter.
   *
   * @param value - Selected association type or null to clear.
   */
  const handleAssociationTypeChange = (value: string | null = ''): void => {
    const newValue = value ?? '';
    setAssociationType(newValue);

    // Check if all filters will be cleared after this change
    if (!newValue && !actionType && (!dateRange.start || !dateRange.end)) {
      fetchPropertyHistory({ page: 1, limit: limit });
      setPage?.(1);
    }
  };

  /**
   * Updates the action type filter. Triggers a data refresh if
   * this clears the final active filter.
   *
   * @param value - Selected action type or null to clear.
   */
  const handleActionTypeChange = (value: string | null = ''): void => {
    const newValue = value ?? '';
    setActionType(newValue);

    // Check if all filters will be cleared after this change
    if (!newValue && !associationType && (!dateRange.start || !dateRange.end)) {
      fetchPropertyHistory({ page: 1, limit: limit });
      setPage?.(1);
    }
  };

  /**
   * Updates the date range filter and resets state if both dates are cleared.
   * Triggers a data refresh when no filters remain.
   *
   * @param range - Partial date range with optional `start` and `end` (YYYY-MM-DD).
   */
  const handleDateRangeChange = (range: IDateRange): void => {
    setDateRange(range);

    // Check if all filters will be cleared after this change
    if (!range.start && !range.end && !associationType && !actionType) {
      setDateRange({ start: undefined, end: undefined });
      fetchPropertyHistory({ page: 1, limit: limit });
      setPage?.(1);
    }
  };

  const associationTypeOptions = Object.values(EAssociationType);
  const actionTypeOptions = Object.values(EPropertyActionType);

  const startDateValue = dateRange?.start ? dayjs(dateRange.start) : null;
  const endDateValue = dateRange?.end ? dayjs(dateRange.end) : null;

  // Set max date as today
  const maxDate = dayjs();

  // Calculate max end date based on start date
  /**
   * Calculates the maximum allowed end date based on the selected start date:
   * up to 90 days after start, but not beyond today.
   *
   * @returns Max end date formatted as YYYY-MM-DD.
   */
  const calculateMaxEndDate = (): string => {
    if (!startDateValue) {
      return maxDate.format('YYYY-MM-DD');
    }
    const maxEndDateFromStart = startDateValue.add(90, 'day');
    return maxEndDateFromStart.isAfter(maxDate)
      ? maxDate.format('YYYY-MM-DD')
      : maxEndDateFromStart.format('YYYY-MM-DD');
  };

  /**
   * Handles changes to the start date input, validating the value and updating
   * the date range while clearing any prior errors.
   *
   * @param event - Change event from the start date text field.
   */
  const onStartDateChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const inputValue = event.target.value;
    const newValue = inputValue ?? undefined;
    setErrorMessage(null);

    if (newValue && !dayjs(newValue).isValid()) {
      return;
    }

    handleDateRangeChange({
      start: newValue,
      end: dateRange?.end,
    });
  };

  /**
   * Handles changes to the end date input, validating the value and updating
   * the date range while clearing any prior errors.
   *
   * @param event - Change event from the end date text field.
   */
  const onEndDateChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const inputValue = event.target.value;
    const newValue = inputValue ?? undefined;
    setErrorMessage(null);

    if (newValue && !dayjs(newValue).isValid()) {
      return;
    }

    handleDateRangeChange({
      start: dateRange?.start,
      end: newValue,
    });
  };

  // Determine if submit button should be enabled
  const hasAssociationType = associationType !== null && associationType !== '';
  const hasActionType = actionType !== null && actionType !== '';
  const hasBothDates = startDateValue !== null && endDateValue !== null;
  const hasOnlyOneDate =
    (startDateValue !== null && endDateValue === null) ||
    (startDateValue === null && endDateValue !== null);

  // Enable submit if: (associationType OR actionType OR both dates) AND NOT only one date
  const isSubmitEnabled = (hasAssociationType || hasActionType || hasBothDates) && !hasOnlyOneDate;

  /**
   * Validates the selected filters (date formats, range order, max span, completeness)
   * and dispatches a history fetch when valid.
   */
  const handleSubmit = (): void => {
    // Validate dates if both are provided
    if (startDateValue && endDateValue) {
      // Validate that both dates are valid
      if (!startDateValue.isValid() || !endDateValue.isValid()) {
        setErrorMessage(t('messages.invalidDateFormat'));
        return;
      }

      // Validate that end date is not before start date
      if (endDateValue.isBefore(startDateValue)) {
        setErrorMessage(t('messages.endDateMustBeAfterStartDate'));
        return;
      }

      // Validate that the difference is at most 90 days
      const daysDifference = endDateValue.diff(startDateValue, 'day');
      if (daysDifference > 90) {
        setErrorMessage(t('messages.dateRangeExceeds90Days'));
        return;
      }
    }

    // Validate that if one date is provided, both should be provided
    if ((startDateValue && !endDateValue) || (!startDateValue && endDateValue)) {
      setErrorMessage(t('messages.bothDatesRequired'));
      return;
    }

    // All validations passed
    setErrorMessage(null);
    setPage?.(1);
    fetchPropertyHistory({
      page: 1,
      limit: limit ?? ERecordLength.RL15,
      associationType: associationType || undefined,
      action: actionType || undefined,
      startDate: startDateValue ? startDateValue.format('YYYY-MM-DD') : undefined,
      endDate: endDateValue ? endDateValue.format('YYYY-MM-DD') : undefined,
    });
  };

  return (
    <Box className="historyFilterPanel">
      <Box className="historyFilterContent">
        <Box className="historyFilterRow">
          {!property && (
            <FormControl size="small" className="historyFilterFormControl">
              <InputLabel id="association-type-label">{t('labels.associationType')}</InputLabel>
              <Select
                labelId="association-type-label"
                id="association-type-select"
                data-testid="association-type-select"
                value={associationType ?? ''}
                label={t('labels.associationType')}
                onChange={(e): void => handleAssociationTypeChange(e.target.value)}
                renderValue={(selected): ReactElement => (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <span>{selected}</span>
                    {selected && (
                      <IconButton
                        size="small"
                        onMouseDown={(e): void => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAssociationTypeChange(null);
                        }}
                        sx={{ padding: '2px', marginRight: '-8px' }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                )}
              >
                {associationTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl size="small" className="historyFilterFormControl">
            <InputLabel id="action-type-label">{t('labels.action')}</InputLabel>
            <Select
              labelId="action-type-label"
              id="action-type-select"
              data-testid="action-type-select"
              value={actionType ?? ''}
              label={t('labels.action')}
              onChange={(e): void => handleActionTypeChange(e.target.value)}
              renderValue={(selected): ReactElement => (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <span>{selected}</span>
                  {selected && (
                    <IconButton
                      size="small"
                      onMouseDown={(e): void => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleActionTypeChange(null);
                      }}
                      sx={{ padding: '2px', marginRight: '-8px' }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}
            >
              {actionTypeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box className="historyFilterRow">
          <TextField
            type="date"
            label={t('labels.startDate')}
            value={startDateValue?.isValid() ? startDateValue.format('YYYY-MM-DD') : ''}
            onChange={onStartDateChange}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: {
                min: endDateValue
                  ? endDateValue.subtract(90, 'day').format('YYYY-MM-DD')
                  : undefined,
                max: endDateValue
                  ? endDateValue.format('YYYY-MM-DD')
                  : maxDate.format('YYYY-MM-DD'),
                placeholder: '',
              },
              input: {
                endAdornment: startDateValue && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={(): void => {
                        handleDateRangeChange({ start: undefined, end: dateRange?.end });
                        setErrorMessage(null);
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            size="small"
          />

          <TextField
            type="date"
            label={t('labels.endDate')}
            value={endDateValue?.isValid() ? endDateValue.format('YYYY-MM-DD') : ''}
            onChange={onEndDateChange}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: {
                min: startDateValue ? startDateValue.format('YYYY-MM-DD') : undefined,
                max: calculateMaxEndDate(),
                placeholder: '',
              },
              input: {
                endAdornment: endDateValue && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={(): void => {
                        handleDateRangeChange({ start: dateRange?.start, end: undefined });
                        setErrorMessage(null);
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            size="small"
          />
        </Box>
        <Button
          className="common-btn"
          variant="contained"
          onClick={handleSubmit}
          size="small"
          disabled={!isSubmitEnabled}
        >
          {t('button.submit')}
        </Button>
      </Box>
      {errorMessage && <FormHelperText className="color-red">{errorMessage}</FormHelperText>}
    </Box>
  );
};
